import type { FeatureCollection } from 'geojson';
import {
  HALL3_CENTER,
  HALL3_BEARING,
  HALL3_SIZE_M,
  HALL3_FLOOR_IMAGE,
  HALL3_FLOOR_COORDS,
  HALL3_ENTRANCES,
  HALL3_STANDS,
  HALL3_WALLS,
} from './hall3Layout';
import {
  HALL5_CENTER,
  HALL5_BEARING,
  HALL5_SIZE_M,
  HALL5_FLOOR_IMAGE,
  HALL5_FLOOR_COORDS,
  HALL5_ENTRANCES,
  HALL5_STANDS,
  HALL5_WALLS,
} from './hall5Layout';
import {
  HALL6_CENTER,
  HALL6_BEARING,
  HALL6_SIZE_M,
  HALL6_FLOOR_IMAGE,
  HALL6_FLOOR_COORDS,
  HALL6_ENTRANCES,
  HALL6_STANDS,
  HALL6_WALLS,
} from './hall6Layout';
import {
  HALL7_CENTER,
  HALL7_BEARING,
  HALL7_SIZE_M,
  HALL7_FLOOR_IMAGE,
  HALL7_FLOOR_COORDS,
  HALL7_ENTRANCES,
  HALL7_STANDS,
  HALL7_WALLS,
} from './hall7Layout';
import {
  HALL10_CENTER,
  HALL10_BEARING,
  HALL10_SIZE_M,
  HALL10_FLOOR_IMAGE,
  HALL10_FLOOR_COORDS,
  HALL10_ENTRANCES,
  HALL10_STANDS,
  HALL10_WALLS,
} from './hall10Layout';
import {
  HALL15_CENTER,
  HALL15_BEARING,
  HALL15_SIZE_M,
  HALL15_FLOOR_IMAGE,
  HALL15_FLOOR_COORDS,
  HALL15_ENTRANCES,
  HALL15_STANDS,
  HALL15_WALLS,
} from './hall15Layout';
import {
  HALL16_CENTER,
  HALL16_BEARING,
  HALL16_SIZE_M,
  HALL16_FLOOR_IMAGE,
  HALL16_FLOOR_COORDS,
  HALL16_ENTRANCES,
  HALL16_STANDS,
  HALL16_WALLS,
} from './hall16Layout';
import {
  HALL17_CENTER,
  HALL17_BEARING,
  HALL17_SIZE_M,
  HALL17_FLOOR_IMAGE,
  HALL17_FLOOR_COORDS,
  HALL17_ENTRANCES,
  HALL17_STANDS,
  HALL17_WALLS,
} from './hall17Layout';

/**
 * A pavilion with an indoor 3D plan that can be entered from its map marker.
 *
 * One entry per hall. To add another, generate its layout module with
 * scripts/extract_pavilion_layout.py and register it in HALL_PLANS below —
 * nothing else needs to change.
 */
export interface HallPlan {
  /** ΔΕΘ pavilion number, matched against a map point's metadata.pavilion. */
  pavilion: number;
  /** Display name, used on the button that opens the plan. */
  name: string;
  center: [number, number];
  /** Compass bearing of the plan's up axis; the map rotates to this. */
  bearing: number;
  /** Real footprint size in metres, [across-plan, along-plan]. */
  sizeM: [number, number];
  floorImage: string;
  floorCoords: [number, number][];
  entrances: [number, number][];
  stands: FeatureCollection;
  walls: FeatureCollection;
  cameras: HallCameraDef[];
}

/** A camera mounted on a stand inside a hall. */
export interface HallCameraDef {
  /** Stand label the camera sits on; its centroid becomes the marker position. */
  stand: string;
  /**
   * Name of the `camera` sensor that carries this camera's RTMP URL.
   *
   * The stream URL deliberately lives on the sensor row rather than here, so it
   * stays editable from the sensor panel and there is one source of truth. If the
   * sensor is missing or has no rtmp_url, the camera simply has no stream button.
   */
  sensorName?: string;
}

export const HALL_PLANS: Record<number, HallPlan> = {
  3: {
    pavilion: 3,
    name: 'Περίπτερο 3',
    center: HALL3_CENTER,
    bearing: HALL3_BEARING,
    sizeM: HALL3_SIZE_M,
    floorImage: HALL3_FLOOR_IMAGE,
    floorCoords: HALL3_FLOOR_COORDS,
    entrances: HALL3_ENTRANCES,
    stands: HALL3_STANDS,
    walls: HALL3_WALLS,
    // Stand assignment is a demo mapping onto the existing ΔΕΘ camera sensors —
    // adjust sensorName here if a camera should follow a different feed.
    cameras: [
      { stand: 'A05', sensorName: 'ΔΕΘ Cam 1' },
      { stand: 'C05', sensorName: 'ΔΕΘ Cam 2' },
      { stand: 'B01', sensorName: 'ΔΕΘ Family Entrance' },
    ],
  },
  5: {
    pavilion: 5,
    name: 'Περίπτερο 5',
    center: HALL5_CENTER,
    bearing: HALL5_BEARING,
    sizeM: HALL5_SIZE_M,
    floorImage: HALL5_FLOOR_IMAGE,
    floorCoords: HALL5_FLOOR_COORDS,
    entrances: HALL5_ENTRANCES,
    stands: HALL5_STANDS,
    walls: HALL5_WALLS,
    cameras: [],
  },
  6: {
    pavilion: 6,
    name: 'Περίπτερο 6',
    center: HALL6_CENTER,
    bearing: HALL6_BEARING,
    sizeM: HALL6_SIZE_M,
    floorImage: HALL6_FLOOR_IMAGE,
    floorCoords: HALL6_FLOOR_COORDS,
    entrances: HALL6_ENTRANCES,
    stands: HALL6_STANDS,
    walls: HALL6_WALLS,
    cameras: [],
  },
  7: {
    pavilion: 7,
    name: 'Περίπτερο 7',
    center: HALL7_CENTER,
    bearing: HALL7_BEARING,
    sizeM: HALL7_SIZE_M,
    floorImage: HALL7_FLOOR_IMAGE,
    floorCoords: HALL7_FLOOR_COORDS,
    entrances: HALL7_ENTRANCES,
    stands: HALL7_STANDS,
    walls: HALL7_WALLS,
    cameras: [],
  },
  10: {
    pavilion: 10,
    name: 'Περίπτερο 10',
    center: HALL10_CENTER,
    bearing: HALL10_BEARING,
    sizeM: HALL10_SIZE_M,
    floorImage: HALL10_FLOOR_IMAGE,
    floorCoords: HALL10_FLOOR_COORDS,
    entrances: HALL10_ENTRANCES,
    stands: HALL10_STANDS,
    walls: HALL10_WALLS,
    cameras: [],
  },
  15: {
    pavilion: 15,
    name: 'Περίπτερο 15',
    center: HALL15_CENTER,
    bearing: HALL15_BEARING,
    sizeM: HALL15_SIZE_M,
    floorImage: HALL15_FLOOR_IMAGE,
    floorCoords: HALL15_FLOOR_COORDS,
    entrances: HALL15_ENTRANCES,
    stands: HALL15_STANDS,
    walls: HALL15_WALLS,
    cameras: [],
  },
  16: {
    pavilion: 16,
    name: 'Περίπτερο 16',
    center: HALL16_CENTER,
    bearing: HALL16_BEARING,
    sizeM: HALL16_SIZE_M,
    floorImage: HALL16_FLOOR_IMAGE,
    floorCoords: HALL16_FLOOR_COORDS,
    entrances: HALL16_ENTRANCES,
    stands: HALL16_STANDS,
    walls: HALL16_WALLS,
    cameras: [],
  },
  17: {
    pavilion: 17,
    name: 'Περίπτερο 17',
    center: HALL17_CENTER,
    bearing: HALL17_BEARING,
    sizeM: HALL17_SIZE_M,
    floorImage: HALL17_FLOOR_IMAGE,
    floorCoords: HALL17_FLOOR_COORDS,
    entrances: HALL17_ENTRANCES,
    stands: HALL17_STANDS,
    walls: HALL17_WALLS,
    cameras: [],
  },
};

/**
 * The 3D plan for a map point, or null if that pavilion has no plan yet.
 *
 * Points imported from the OSM survey carry metadata.pavilion. Points created
 * by hand usually do not, so the name is used as a fallback — it is the only
 * other place the hall number appears.
 */
export function hallPlanForPoint(point: {
  name?: string;
  metadata?: { pavilion?: unknown } | null;
} | null | undefined): HallPlan | null {
  if (!point) return null;

  const tagged = Number(point.metadata?.pavilion);
  if (Number.isInteger(tagged) && HALL_PLANS[tagged]) return HALL_PLANS[tagged];

  const named = point.name?.match(/(?:Περίπτερο|Pavilion|Hall)\s*(\d{1,2})\b/i);
  if (named) {
    const n = Number(named[1]);
    if (HALL_PLANS[n]) return HALL_PLANS[n];
  }
  return null;
}
